import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Button,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  LinkSimple,
  WarningCircle,
  Warning,
  CheckCircle,
  Percent,
  ArrowSquareOut,
} from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct } from '../format';
import { IAuditoriaEmpresa, ICruzamentos, AuditoriaArea } from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { UI } from '../ui';

interface CruzamentosScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Drill: abre o Painel de Auditorias filtrado pela área da regra clicada. */
  onOpenAuditoria: (area: AuditoriaArea, focusKey?: string) => void;
  /** Cruzamento: abre o submódulo-fonte da área (Integração, Notas, Pessoal...). */
  onScreen: (screen: string) => void;
}

/** Área → submódulo-fonte para o atalho de cruzamento. */
const AREA_TO_SCREEN: Record<string, { seg: string; label: string }> = {
  fc: { seg: 'integracao', label: 'Integração Fiscal-Contábil' },
  ff: { seg: 'pessoal', label: 'Pessoal' },
  fo: { seg: 'obrigacoes', label: 'Obrigações Acessórias' },
  nf: { seg: 'notas', label: 'Notas Fiscais' },
  sn: { seg: 'simples', label: 'Simples Nacional' },
};

const AREA_META: Record<AuditoriaArea, { label: string; desc: string; color: string }> = {
  fc: {
    label: 'Fiscal × Contábil',
    desc: 'Confronto entre os registros fiscais e o que foi lançado na contabilidade.',
    color: UI.brand,
  },
  ff: {
    label: 'Fiscal × Folha',
    desc: 'Cruzamento entre apuração fiscal e os encargos da folha de pagamento.',
    color: UI.pos,
  },
  fo: {
    label: 'Folha × Obrigações',
    desc: 'Conferência entre a folha e as obrigações acessórias entregues.',
    color: UI.warn,
  },
  nf: {
    label: 'Notas × Fiscal',
    desc: 'Comparação entre as notas emitidas/recebidas e a apuração fiscal.',
    color: '#8B5CF6',
  },
  sn: {
    label: 'Simples × Notas',
    desc: 'Confronto entre a base do Simples Nacional e o volume de notas.',
    color: '#1AA6B7',
  },
};

export function CruzamentosScreen({
  empresa,
  periodo,
  onOpenAuditoria,
  onScreen,
}: CruzamentosScreenProps) {
  const { getCruzamentos } = useAuditoriaInteligente();
  const [tab, setTab] = useState<AuditoriaArea | null>(null);

  const { data, isFetching } = useQuery<ICruzamentos>({
    queryKey: ['ai-cruzamentos', { id: empresa?.id, ...periodo }],
    queryFn: getCruzamentos,
    enabled: !!empresa,
  });

  const areas = useMemo(() => data?.por_area ?? [], [data]);

  // Seleciona a 1ª aba assim que os dados chegam (ou se a aba atual sumir).
  useEffect(() => {
    if (areas.length && (!tab || !areas.some(a => a.area === tab))) {
      setTab(areas[0].area);
    }
  }, [areas, tab]);

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver os cruzamentos por área."
        icon={LinkSimple}
      />
    );
  }

  if (isFetching && !data) {
    return (
      <Flex justify="center" py={16}>
        <Spinner />
      </Flex>
    );
  }

  const areaAtual = areas.find(a => a.area === tab);
  const meta = tab ? AREA_META[tab] : undefined;
  const regras = (data?.regras ?? []).filter(r => r.area === tab);
  const somaDif = regras.reduce((s, r) => s + Math.abs(num(r.diferenca)), 0);
  const conformidade = areaAtual && areaAtual.total ? (areaAtual.ok / areaAtual.total) * 100 : 0;
  const destino = tab ? AREA_TO_SCREEN[tab] : undefined;

  // Cada regra compara "Lado A vs Lado B" (na própria descrição) — valor_a = lado
  // esquerdo, valor_b = lado direito. Deriva os nomes reais p/ acabar com o "A/B"
  // opaco. Se toda a área usa o mesmo par, vira cabeçalho; senão, legenda por linha.
  const ladosDe = (desc: string): [string, string] => {
    const [a, b] = (desc || '').split(/\s+vs\.?\s+/i);
    return [a?.trim() ?? '', b?.trim() ?? ''];
  };
  const uniq = (xs: string[]) => Array.from(new Set(xs.filter(Boolean)));
  const ladosA = uniq(regras.map(r => ladosDe(r.descricao)[0]));
  const ladosB = uniq(regras.map(r => ladosDe(r.descricao)[1]));
  const labelA = ladosA.length === 1 ? ladosA[0] : null;
  const labelB = ladosB.length === 1 ? ladosB[0] : null;

  return (
    <Box>
      {/* Abas por área */}
      <Flex gap={1} borderBottom="1px solid #E7ECF2" mb={5} wrap="wrap">
        {areas.map(a => {
          const isActive = a.area === tab;
          const m = AREA_META[a.area];
          return (
            <Flex
              key={a.area}
              align="center"
              gap={2}
              px={4}
              py={3}
              cursor="pointer"
              onClick={() => setTab(a.area)}
              borderBottom="2.5px solid"
              borderColor={isActive ? UI.brand : 'transparent'}
              color={isActive ? UI.brand : UI.muted}
              mb="-1px"
              _hover={{ color: isActive ? UI.brand : UI.ink }}
            >
              <Text fontSize="13px" fontWeight={700}>
                {m?.label ?? a.area}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      {meta && (
        <Flex
          align="center"
          gap={3}
          p={4}
          mb={5}
          borderRadius="14px"
          bg={`${meta.color}12`}
          border={`1px solid ${meta.color}33`}
        >
          <Flex
            align="center"
            justify="center"
            w="38px"
            h="38px"
            borderRadius="11px"
            bg="white"
            color={meta.color}
            flexShrink={0}
          >
            <LinkSimple size={20} />
          </Flex>
          <Box flex={1} minW={0}>
            <Text fontWeight={700} color={UI.ink}>
              {meta.label}
            </Text>
            <Text fontSize="12.5px" color={UI.muted}>
              {meta.desc}
            </Text>
          </Box>
          <Box textAlign="right" flexShrink={0} display={{ base: 'none', md: 'block' }}>
            <Text
              fontSize="10px"
              fontWeight={800}
              color={UI.faint}
              textTransform="uppercase"
              letterSpacing="0.08em"
            >
              Σ diferença
            </Text>
            <Text fontSize="15px" fontWeight={800} color={somaDif > 0 ? UI.neg : UI.pos}>
              {formatCurrency(somaDif)}
            </Text>
          </Box>
          {destino && (
            <Button
              size="sm"
              variant="outline"
              flexShrink={0}
              borderColor={meta.color}
              color={meta.color}
              leftIcon={<ArrowSquareOut size={14} weight="bold" />}
              _hover={{ bg: `${meta.color}14` }}
              onClick={() => onScreen(destino.seg)}
            >
              Abrir {destino.label}
            </Button>
          )}
        </Flex>
      )}

      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
        <KpiCard
          label="Divergências"
          value={areaAtual?.divergencias ?? 0}
          icon={WarningCircle}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          info={
            <InfoBloco
              oque="Quantidade de verificações da área selecionada cujo cruzamento entre as duas bases acusou divergência (status DIVERGENCIA)."
              dados="todas_regras (motor de auditoria, audit_cte): COUNT FILTER status='DIVERGENCIA', agrupado por área"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Alertas"
          value={areaAtual?.alertas ?? 0}
          icon={Warning}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          info={
            <InfoBloco
              oque="Quantidade de verificações da área selecionada que ficaram em alerta — diferença dentro de tolerância ou pendência que exige atenção (status ALERTA)."
              dados="todas_regras (motor de auditoria, audit_cte): COUNT FILTER status='ALERTA', agrupado por área"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Conferidos"
          value={areaAtual?.ok ?? 0}
          icon={CheckCircle}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          info={
            <InfoBloco
              oque="Quantidade de verificações da área selecionada que bateram entre as duas bases, sem divergência nem alerta (status OK)."
              dados="todas_regras (motor de auditoria, audit_cte): COUNT FILTER status='OK', agrupado por área"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Conformidade"
          value={pct(conformidade)}
          sub={`${areaAtual?.ok ?? 0}/${areaAtual?.total ?? 0} conferências`}
          icon={Percent}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          info={
            <InfoBloco
              oque="Percentual de conferências OK sobre o total da área selecionada — quanto do cruzamento bateu sem ressalvas."
              dados="todas_regras (motor de auditoria, audit_cte): ok ÷ total (COUNT FILTER status='OK' / COUNT total) por área"
              filtro={FILTRO.empresa}
            />
          }
        />
      </Grid>

      <Box
        borderRadius="18px"
        overflow="hidden"
        bg="rgba(255,255,255,0.7)"
        backdropFilter="blur(14px) saturate(160%)"
        border="1px solid rgba(255,255,255,0.85)"
        boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
      >
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={UI.rowHover}>
              <Tr>
                <Th>Status</Th>
                <Th>Regra</Th>
                <Th>Descrição</Th>
                <Th>Período</Th>
                <Th isNumeric>{labelA ?? 'Valor A'}</Th>
                <Th isNumeric>{labelB ?? 'Valor B'}</Th>
                <Th isNumeric>Diferença</Th>
              </Tr>
            </Thead>
            <Tbody>
              {regras.map(r => {
                const [la, lb] = ladosDe(r.descricao);
                return (
                  <Tr
                    key={r.id}
                    cursor="pointer"
                    _hover={{ bg: UI.rowHover }}
                    onClick={() => onOpenAuditoria(r.area, `${r.area}-${r.regra}-${r.periodo}`)}
                    title="Abrir este apontamento no Painel de Auditorias"
                  >
                    <Td>
                      <StatusBadge status={r.status} badge={r.badge} />
                    </Td>
                    <Td fontWeight={700}>{r.regra}</Td>
                    <Td maxW="320px" whiteSpace="normal">
                      {r.descricao}
                    </Td>
                    <Td>{r.periodo}</Td>
                    <Td isNumeric>
                      {!labelA && la && (
                        <Text
                          fontSize="9.5px"
                          color={UI.faint}
                          textTransform="uppercase"
                          lineHeight="1.2"
                        >
                          {la}
                        </Text>
                      )}
                      <Text color={UI.muted}>{formatCurrency(r.valor_a)}</Text>
                    </Td>
                    <Td isNumeric>
                      {!labelB && lb && (
                        <Text
                          fontSize="9.5px"
                          color={UI.faint}
                          textTransform="uppercase"
                          lineHeight="1.2"
                        >
                          {lb}
                        </Text>
                      )}
                      <Text color={UI.muted}>{formatCurrency(r.valor_b)}</Text>
                    </Td>
                    <Td isNumeric fontWeight={700} color={num(r.diferenca) ? UI.neg : UI.muted}>
                      {num(r.diferenca) ? formatCurrency(r.diferenca) : '—'}
                    </Td>
                  </Tr>
                );
              })}
              {regras.length === 0 && (
                <Tr>
                  <Td colSpan={7} textAlign="center" py={8} color={UI.muted}>
                    Nenhuma regra para esta área no período.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
